<template>
  <section class="order-detail-pane">
    <div v-if="!selectedOrder" class="detail-empty">点击左侧订单查看详情</div>
    <div v-else class="order-detail-scroll">
      <header class="detail-heading">
        <div>
          <h2>生产订单详情</h2>
          <p>{{ order.name || order.productionOrderName || order.code || '' }}</p>
        </div>
        <ASpin v-if="loading" size="small" />
      </header>

      <section class="detail-card">
        <h3>基本信息</h3>
        <div class="fields-grid">
          <div v-for="field in basicFields" :key="field.label" class="detail-field" :class="{ wide: field.wide }">
            <span class="field-label">{{ field.label }}</span>
            <strong>{{ displayValue(field.value) }}</strong>
          </div>
        </div>
      </section>

      <section v-if="firstImage && !imageLoadFailed" class="order-image-card" @click="previewImage">
        <img :src="imageUrl" alt="产品图片 / 原始订单截图" @error="imageLoadFailed = true">
        <div>
          <strong>产品图片 / 原始订单截图</strong>
          <span>点击放大核对产品图与工艺备注</span>
          <span>当前图片取自提供的订单截图</span>
        </div>
      </section>
      <section v-else class="order-image-card image-empty-card">
        <span class="image-placeholder">暂无图片</span>
        <div>
          <strong>产品图片 / 原始订单截图</strong>
          <span>当前订单没有图片</span>
        </div>
      </section>

      <section class="detail-card">
        <h3>产品明细 · {{ materials.length }} 项</h3>
        <div v-for="(material, materialIndex) in materials" :key="material.id || materialIndex" class="material-block">
          <div v-if="materials.length > 1" class="material-title">产品 {{ materialIndex + 1 }}</div>
          <div class="fields-grid">
            <div v-for="field in materialFields(material)" :key="field.label" class="detail-field">
              <span class="field-label">{{ field.label }}</span>
              <strong>{{ displayValue(field.value) }}</strong>
            </div>
          </div>
        </div>
        <div v-if="!materials.length" class="detail-empty-inline">暂无产品明细</div>
      </section>

      <section class="detail-card">
        <h3>工艺要求 · 膜材信息</h3>
        <div class="fields-grid">
          <div v-for="field in filmFields" :key="field.label" class="detail-field" :class="{ wide: field.wide }">
            <span class="field-label">{{ field.label }}</span>
            <strong>{{ displayValue(field.value) }}</strong>
          </div>
        </div>
      </section>

      <section v-if="order.craftRouteName" class="detail-card other-requirements">
        <h3>工艺路线</h3>
        <p>{{ displayValue(order.craftRouteName) }}</p>
      </section>

      <div class="process-grid">
        <section v-for="section in processSections" :key="section.id" class="detail-card process-card"
          :class="{ wide: section.wide }">
          <h3>{{ section.title }}</h3>
          <div v-if="section.checks.length" class="check-list">
            <span v-for="check in section.checks" :key="check.label" class="check-item" :class="{ selected: check.selected }">
              <i>{{ check.selected ? '✓' : '' }}</i>{{ check.label }}
            </span>
          </div>
          <div v-if="section.fields.length" class="process-fields">
            <div v-for="field in section.fields" :key="field.label" class="detail-field" :class="{ wide: field.wide }">
              <span class="field-label">{{ field.label }}</span>
              <strong>{{ displayValue(field.value) }}</strong>
            </div>
          </div>
        </section>
      </div>

      <section v-if="order.craftRouteRemark" class="detail-card other-requirements">
        <h3>其他要求</h3>
        <p>{{ order.craftRouteRemark }}</p>
      </section>
      <div v-if="loadError" class="load-error">详情加载失败，请稍后重试。</div>
    </div>
  </section>
</template>

<script>
import { dataAuthOrdersId } from '@api'
import publicJs from '@/utils/js/publicJs'

export default {
  name: 'OrderDetail',
  props: {
    selectedOrder: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      detailData: {},
      loading: false,
      loadError: false,
      imageLoadFailed: false,
      requestId: 0,
    }
  },
  computed: {
    order() {
      return this.detailData || this.selectedOrder || {}
    },
    materials() {
      const list = this.order.productionOrder2Materiels || this.order.materials
      return Array.isArray(list) ? list : []
    },
    firstImage() {
      const list = Array.isArray(this.order.imgsData) ? this.order.imgsData : []
      return list[0] || null
    },
    imageUrl() {
      const url = this.firstImage && this.firstImage.url
      if (!url || /^https?:\/\//.test(url)) return url
      return `${String(publicJs.getServerUrl() || '').replace(/\/$/, '')}/${String(url).replace(/^\//, '')}`
    },
    basicFields() {
      const status = this.order.productionOrderStatus || {}
      const priority = this.order.priority || {}
      return [
        { label: '订单编码', value: this.order.code || this.order.productionOrderCode },
        { label: '订单名称', value: this.order.name || this.order.productionOrderName },
        { label: '订单状态', value: status.name || status.code },
        { label: '产品状态', value: (this.order.productStatus || {}).name },
        { label: '客户', value: this.order.customer },
        { label: '优先级', value: priority.name || priority.code || priority },
        { label: '计划交期', value: this.order.submitDate },
        { label: '创建时间', value: this.order.createDate },
        { label: '制单人员', value: this.order.creator },
        { label: '订单备注', value: this.order.remark },
      ]
    },
    filmFields() {
      return [
        { label: '正反版数', value: this.order.frontAndBack },
        { label: '正面版数', value: this.order.front },
        { label: '反面版数', value: this.order.back },
        { label: '膜型', value: this.order.membraneType },
        { label: '质量', value: this.order.quality },
        { label: '袋型', value: this.order.bagType },
        { label: '质感', value: this.order.texture },
        { label: '颜色', value: this.order.color },
        { label: '膜材备注', value: this.order.membraneRemark, wide: true },
      ]
    },
    processSections() {
      const sections = []
      const processes = Array.isArray(this.order.processVOList) ? this.order.processVOList.slice() : []
      processes.sort((first, second) => Number(first.sequence || 0) - Number(second.sequence || 0))
      processes.forEach((process, index) => {
        const customFields = Array.isArray(process.processCustomFieldValList) ? process.processCustomFieldValList : []
        sections.push({
          id: process.id || `process-${index}`,
          title: process.name || `工序 ${index + 1}`,
          checks: customFields.filter(field => field.checkbox).map(field => ({
            label: field.label,
            selected: field.checkboxVal === true || field.checkboxVal === 1 || field.checkboxVal === 'true',
          })),
          fields: customFields.filter(field => field.inputBox).map(field => ({
            label: field.label,
            value: [field.inputBoxVal, field.inputBoxValSuffix].filter(value => value !== null && value !== undefined && value !== '').join(' '),
            wide: String(field.label || '').length > 8,
          })),
        })
      })
      return sections
    },
  },
  watch: {
    selectedOrder: {
      immediate: true,
      handler(order) {
        this.detailData = order || {}
        this.loadError = false
        this.imageLoadFailed = false
        if (order && order.id) this.loadDetail(order.id)
        else this.requestId += 1
      },
    },
  },
  methods: {
    async loadDetail(id) {
      const requestId = ++this.requestId
      this.loading = true
      this.loadError = false
      try {
        const response = await dataAuthOrdersId(id)
        let detail = response && response.data !== undefined ? response.data : response
        if (detail && detail.data && !detail.imgsData) detail = detail.data
        if (requestId !== this.requestId) return
        if (detail && typeof detail === 'object') this.detailData = { ...this.selectedOrder, ...detail }
        console.log('dataAuthOrdersId 返回结果：', response)
      } catch (error) {
        if (requestId === this.requestId) {
          this.loadError = true
          this.$message.error('订单详情加载失败，请稍后重试。')
        }
      } finally {
        if (requestId === this.requestId) this.loading = false
      }
    },
    materialFields(material) {
      return [
        { label: '型号', value: material.model },
        { label: '膜宽 (cm)', value: material.width },
        { label: '膜长 (cm)', value: material.length },
        { label: '膜厚 (mm)', value: material.thickness },
        { label: '克重 (g)', value: material.grammage },
        { label: '抽边净剩 (cm)', value: material.remainder },
        { label: '换算米数 (m)', value: material.conversion },
        { label: '条数', value: material.rowNum },
        { label: '订单数量', value: material.orderNum },
        { label: '计划数量', value: material.planNum },
        { label: '已排产数量', value: material.arrangeNum },
        { label: '批次号', value: material.batchCode },
      ]
    },
    displayValue(value) {
      if (value === null || value === undefined || value === '') return '未填写'
      if (typeof value === 'object') return value.name || value.code || '未填写'
      return value
    },
    previewImage() {
      if (!this.imageUrl || this.imageLoadFailed) return
      window.open(this.imageUrl, '_blank')
    },
  },
}
</script>

<style lang="less" scoped>
.order-detail-pane {
  min-width: 0;
  min-height: 0;
  padding: 16px;
  overflow: hidden;
  background: #edf2f7;
}

.order-detail-scroll {
  height: 100%;
  overflow-y: auto;
  padding-right: 4px;
}

.detail-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.detail-heading h2 {
  margin: 0;
  color: #123f63;
  font-size: 24px;
}

.detail-heading p {
  margin: 5px 0 0;
  color: #67839a;
  font-size: 16px;
}

.detail-card,
.order-image-card {
  margin-bottom: 12px;
  overflow: hidden;
  background: #fff;
  border: 1px solid #d4e0e8;
  border-radius: 14px;
}

.detail-card h3 {
  margin: 0;
  padding: 13px 15px;
  color: #123f63;
  font-size: 18px;
  border-bottom: 1px solid #e3ebf0;
}

.fields-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.detail-field {
  display: grid;
  gap: 5px;
  min-width: 0;
  min-height: 62px;
  padding: 10px 14px;
  overflow-wrap: anywhere;
  border-bottom: 1px solid #e6edf2;
}

.detail-field.wide {
  grid-column: 1 / -1;
}

.field-label {
  color: #67839a;
  font-size: 14px;
}

.detail-field strong {
  color: #173d5b;
  font-size: 16px;
  line-height: 1.45;
}

.order-image-card {
  display: flex;
  align-items: center;
  gap: 14px;
  min-height: 110px;
  padding: 12px;
  cursor: pointer;
}

.order-image-card img,
.image-placeholder {
  display: flex;
  flex: 0 0 100px;
  align-items: center;
  justify-content: center;
  width: 100px;
  height: 84px;
  object-fit: contain;
  color: #8aa0b1;
  font-size: 14px;
  background: #f7fafc;
  border: 1px solid #dce6ed;
  border-radius: 8px;
}

.image-empty-card {
  cursor: default;
}

.order-image-card > div {
  display: grid;
  gap: 5px;
  color: #67839a;
  font-size: 14px;
}

.order-image-card strong {
  color: #173d5b;
  font-size: 17px;
}

.material-block + .material-block {
  border-top: 1px solid #dce5ee;
}

.material-title {
  padding: 10px 14px 0;
  color: #315672;
  font-size: 16px;
  font-weight: 600;
}

.process-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: stretch;
  gap: 12px;
}

.process-card {
  margin-bottom: 0;
  padding-bottom: 10px;
}

.process-card.wide {
  grid-column: 1 / -1;
}

.process-card h3 {
  padding-bottom: 0;
  border-bottom: 0;
}

.check-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
  padding: 12px 14px 0;
}

.check-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #315672;
  font-size: 14px;
}

.check-item i {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 17px;
  height: 17px;
  color: transparent;
  font-size: 14px;
  font-style: normal;
  border: 1px solid #a7b9c8;
  border-radius: 4px;
}

.check-item.selected i {
  color: #fff;
  background: #1678dc;
  border-color: #1678dc;
}

.process-fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 4px 0;
  margin-top: 8px;
}

.process-fields .detail-field {
  min-height: 0;
  padding-top: 6px;
  padding-bottom: 6px;
  border-bottom: 0;
}

.other-requirements {
  margin-top: 12px;
}

.other-requirements p {
  margin: 0;
  padding: 15px;
  color: #173d5b;
  font-size: 16px;
  line-height: 1.6;
  overflow-wrap: anywhere;
}

.detail-empty,
.detail-empty-inline,
.load-error {
  padding: 30px;
  color: #67839a;
  font-size: 16px;
  text-align: center;
  background: #fff;
  border: 1px dashed #cbd9e5;
  border-radius: 12px;
}

.detail-empty {
  margin-top: 30px;
}

.load-error {
  padding: 12px;
  color: #b54444;
}

@media (max-width: 900px) {
  .order-detail-pane {
    padding: 12px;
  }

  .process-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .process-card.wide {
    grid-column: auto;
  }
}
</style>
